import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton } from '../../atoms/IconButton'
import { Icon } from '../../atoms/Icon'
import {
  DetailPanelProps,
  DetailPanelHeaderProps,
  DetailPanelContentProps,
  DetailPanelFooterProps,
} from './DetailPanel.types'
import {
  StyledDetailPanel,
  StyledDetailPanelHeader,
  StyledDetailPanelContent,
  StyledDetailPanelFooter,
} from './DetailPanel.styles'

export const DetailPanel: React.FC<DetailPanelProps> = ({
  isOpen,
  onClose,
  children,
  width,
  maxWidth,
  className,
}) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      <StyledDetailPanel
        $isOpen={isOpen}
        $width={width}
        $maxWidth={maxWidth}
        className={className}
      >
        {children}
      </StyledDetailPanel>
    </>
  )
}

export const DetailPanelHeader: React.FC<DetailPanelHeaderProps> = ({
  children,
  onClose,
  className,
}) => {
  const { t } = useTranslation('ui')

  return (
    <StyledDetailPanelHeader className={className}>
      {onClose && (
        <IconButton
          variant="ghost"
          onClick={onClose}
          style={{ marginRight: '16px' }}
          aria-label={t('detailPanel.closePanel')}
        >
          <Icon name="close" size="medium" />
        </IconButton>
      )}
      {children}
    </StyledDetailPanelHeader>
  )
}

export const DetailPanelContent: React.FC<DetailPanelContentProps> = ({
  children,
  className,
}) => {
  return (
    <StyledDetailPanelContent className={className}>
      {children}
    </StyledDetailPanelContent>
  )
}

export const DetailPanelFooter: React.FC<DetailPanelFooterProps> = ({
  children,
  className,
}) => {
  return (
    <StyledDetailPanelFooter className={className}>
      {children}
    </StyledDetailPanelFooter>
  )
}
